/* eslint-disable @typescript-eslint/naming-convention */
import Joi from '@hapi/joi';
import { captureMessage } from '@sentry/browser';
import classNames from 'classnames';
import moment from 'moment';
import { parse as parseQueryString } from 'query-string';
import React from 'react';
import { useSelector } from 'react-redux';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Batch } from '../../../../common/src/domain/batch/batchTableSchema';
import { BatchStates } from '../../../../common/src/enums/BatchStates';
import { InspectionStandardTypes } from '../../../../common/src/enums/InspectionStandardTypes';
import { Roles } from '../../../../common/src/enums/Roles';
import { SurveyStates } from '../../../../common/src/enums/SurveyStates';
import { getInspectionStandardTypeOptions } from '../../customerSettings';
import { useTranslate } from '../../mixins/translate/translate';
import { IValidationComponentState, useOldValidation, ValidationHelpText } from '../../mixins/validation/validation';
import { fetchGet, fetchPost } from '../../store/fetch';
import { IStoreState } from '../../store/IStoreState';
import { MarkdownEditor } from '../common/markdown/MarkdownEditor';
import {
    Alert,
    Content,
    EditPanelButtons,
    EditPanelCell,
    EditPanelDatePicker,
    EditPanelExplanation,
    EditPanelFilterCreateSelect,
    EditPanelInput,
    EditPanelRow,
    EditPanelSection,
    EditPanelSelect,
    EditPanelSwitch,
    PanelBody,
    PanelHeader,
    TopbarCrumb,
    TopbarCrumbActive,
    TopbarLeftOnly,
    Tray,
} from '../elements';
import { ScopePicker, IScopePickerExclusionInput } from './scope/ScopePicker';
import { useScope, saveScope } from './scope/useScope';

type BatchView = Pick<Batch, 'id' | 'inspectionStandardType' | 'name'>;

interface ISurveyAddEditFormState extends IValidationComponentState {
    currentLanguage?: string;
    surveryedOn?: Date | undefined;
    status?: string | undefined;
    inspectionStandardType?: string | undefined;
    objectInspectionStandardType?: string | undefined;
    description?: string | undefined;
    objectId?: string | undefined;
    operatorCompanyId?: string | undefined;
    surveyorCompanyId?: string | undefined;
    ownerCompanyId?: string | undefined;
    objectName?: string | undefined;
    objectCompositionIsVisible?: boolean | undefined;
    isFmecaAvailable: boolean;
    isCraAvailable: boolean;
    batches: BatchView[];
    areThereActiveBatches: boolean;
    batchId?: string | undefined;
    referenceNumber?: string | undefined;
    knownLabels: string[];
    labels: string[];
    reportDeadlineOnDate: string | null;
    submitting: boolean;
    failed: boolean;
    saved: boolean;
    errors: Record<string, string[] | undefined>;
}

type TabName = 'details' | 'scope' | 'explanation';

function hasValidationErrors(messages: unknown): boolean {
    return Array.isArray(messages) && messages.length > 0;
}

function SurveyAddEditComponent(_props?: Record<string, unknown>): JSX.Element {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const location = useLocation();
    const locationQuery = React.useMemo(() => parseQueryString(location.search), [location.search]);
    const queryObjectId = typeof locationQuery.objectId === 'string' ? locationQuery.objectId : undefined;

    const userRole = useSelector((state: IStoreState) => state.authentication.role as Roles);
    const isClient = useSelector((state: IStoreState) => state.authentication.isClient);
    const useBatches = useSelector((state: IStoreState) => state.authentication.useBatches);
    const useNen2767Advanced = useSelector((state: IStoreState) => state.authentication.useNen2767Advanced);
    const useProductTitle = useSelector((state: IStoreState) => state.authentication.useProductTitle);

    const initialState: ISurveyAddEditFormState = {
        surveryedOn: new Date(),
        reportDeadlineOnDate: moment().add(14, 'days').toISOString(),
        description: '',
        submitting: false,
        failed: false,
        saved: false,
        objectId: queryObjectId,
        isFmecaAvailable: true,
        isCraAvailable: false,
        batches: [],
        areThereActiveBatches: true,
        errors: {},
        labels: [],
        knownLabels: [],
    };

    const { validate, formState, handleChange, setFormState, getValidationMessages } = useOldValidation<ISurveyAddEditFormState>({
        initialState,
        getValidatorTypes() {
            return {
                inspectionStandardType:
                    useNen2767Advanced && ((useBatches && (userRole === Roles.Administrator || isClient)) || !useBatches)
                        ? Joi.required()
                        : Joi.allow(null, ''),
            };
        },
        getValidatorData(state: ISurveyAddEditFormState) {
            return {
                surveryedOn: state.surveryedOn,
                status: state.status,
                inspectionStandardType: state.inspectionStandardType,
                description: state.description,
                objectId: state.objectId,
                operatorCompanyId: state.operatorCompanyId,
                surveyorCompanyId: state.surveyorCompanyId,
                isFmecaAvailable: state.inspectionStandardType === InspectionStandardTypes.Fmeca ? state.isFmecaAvailable : false,
                isCraAvailable: state.inspectionStandardType === InspectionStandardTypes.Fmeca ? state.isCraAvailable : false,
                batchId: state.batchId,
                referenceNumber: state.referenceNumber,
                labels: state.labels,
                reportDeadlineOnDate: state.reportDeadlineOnDate,
            };
        },
        async onChanged(value: unknown, stateKey: string) {
            await Promise.resolve();
            // Side effects are handled by handleChangeWithSideEffects below; this hook is reserved for stateKey-specific reactions.
            // We perform side-effects there because we need access to the latest formState through setFormState callbacks.
            void value;
            void stateKey;
        },
    });

    const [activeTab, setActiveTab] = React.useState<TabName>('details');
    const [definedScope, setDefinedScope] = React.useState<string | null>(null);
    const [exclusions, setExclusions] = React.useState<IScopePickerExclusionInput[]>([]);
    const [isInitialScopeLoaded, setIsInitialScopeLoaded] = React.useState<boolean>(!id);

    const scopeData = useScope(id);
    React.useEffect(() => {
        if (!scopeData.isLoading && id && !isInitialScopeLoaded) {
            setDefinedScope(scopeData.definedScope);
            setExclusions(scopeData.exclusions.map((e) => ({ entityType: e.entityType, entityId: e.entityId })));
            setIsInitialScopeLoaded(true);
        }
    }, [scopeData.isLoading, scopeData.definedScope, scopeData.exclusions, id, isInitialScopeLoaded]);

    const handleChangeWithSideEffects = React.useCallback(
        async (value: unknown, stateKey: string) => {
            await handleChange(value, stateKey);
            if (stateKey === 'isFmecaAvailable' && value === false) {
                await handleChange(true, 'isCraAvailable');
            }
            if (stateKey === 'isCraAvailable' && value === false) {
                await handleChange(true, 'isFmecaAvailable');
            }
            if (stateKey === 'batchId') {
                const inspectionStandardType = formState.batches.find((i) => i.id === value)?.inspectionStandardType;
                if (inspectionStandardType) {
                    await handleChange(inspectionStandardType, 'inspectionStandardType');
                }
            }
            if (stateKey === 'surveryedOn' && !id) {
                await handleChange(
                    moment(value as moment.MomentInput)
                        .add(14, 'days')
                        .toISOString(),
                    'reportDeadlineOnDate',
                );
            }
        },
        [handleChange, formState.batches, id],
    );

    // We keep a ref to the latest formState so async callbacks can read the up-to-date snapshot
    // without making formState a dep (which would re-fire effects).
    const formStateRef = React.useRef<ISurveyAddEditFormState>(formState);
    React.useEffect(() => {
        formStateRef.current = formState;
    }, [formState]);

    const loadObject = React.useCallback(
        async (objectId: string | undefined) => {
            if (!objectId) {
                return;
            }
            const url = `/api/objects/${objectId}?fields=name,inspection,inspectionStandardType`;
            const result = await fetchGet(url);
            const json = result.json as { name: string; inspectionStandardType: string } | undefined;
            if (json) {
                const prev = formStateRef.current;
                const next: ISurveyAddEditFormState = {
                    ...prev,
                    objectName: json.name,
                    objectInspectionStandardType: json.inspectionStandardType,
                };
                if ((json.inspectionStandardType as InspectionStandardTypes) === InspectionStandardTypes.Nen2767 && !prev.inspectionStandardType) {
                    next.inspectionStandardType = InspectionStandardTypes.Nen2767;
                }
                setFormState(next);
            }
        },
        [setFormState],
    );

    const loadBatches = React.useCallback(
        async (objectId: string | undefined) => {
            const response = await fetchGet('/api/batcheslist', { objectId, status: BatchStates.Active });
            const json = response.json as { items?: BatchView[] } | undefined;
            const prev = formStateRef.current;
            if (json?.items && json.items.length > 0) {
                setFormState({
                    ...prev,
                    batches: json.items,
                    areThereActiveBatches: true,
                    batchId: json.items[0]!.id,
                    inspectionStandardType: json.items[0]!.inspectionStandardType,
                });
            } else {
                setFormState({ ...prev, areThereActiveBatches: false });
            }
        },
        [setFormState],
    );

    const loadKnownLabels = React.useCallback(async () => {
        const url = '/api/surveys/knownLabels';
        const result = await fetchGet(url);
        const knownLabels = (result?.json as string[] | undefined) ?? [];
        setFormState({ ...formStateRef.current, knownLabels });
    }, [setFormState]);

    const loadSurvey = React.useCallback(async () => {
        if (!id) {
            return;
        }
        const url = `/api/surveys/${id}`;
        const result = await fetchGet(url);
        const json = result.json as (Partial<ISurveyAddEditFormState> & { surveryedOn?: string; objectId?: string }) | undefined;
        if (json) {
            const surveryedOn = json.surveryedOn ? new Date(json.surveryedOn) : new Date();
            setFormState({ ...formStateRef.current, ...json, surveryedOn });
            if (json.objectId) {
                await loadObject(json.objectId);
            }
        }
    }, [id, setFormState, loadObject]);

    // Initial load
    React.useEffect(() => {
        const run = async () => {
            if (id) {
                await loadSurvey();
            } else {
                await loadObject(queryObjectId);
            }
        };
        void run();
        // We intentionally only run this on id / queryObjectId changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, queryObjectId]);

    // Load batches once objectId is known
    React.useEffect(() => {
        if (useBatches && formState.objectId) {
            void loadBatches(formState.objectId);
        }
    }, [useBatches, formState.objectId, loadBatches]);

    React.useEffect(() => {
        void loadKnownLabels();
    }, [loadKnownLabels]);

    function surveyStates() {
        const options = [
            { value: SurveyStates.Open, text: translate(`surveyStatus.${SurveyStates.Open}`) },
            { value: SurveyStates.Preparation, text: translate(`surveyStatus.${SurveyStates.Preparation}`) },
            { value: SurveyStates.Conducting, text: translate(`surveyStatus.${SurveyStates.Conducting}`) },
            { value: SurveyStates.Reporting, text: translate(`surveyStatus.${SurveyStates.Reporting}`) },
        ];

        if ([Roles.Administrator].includes(userRole) || isClient) {
            options.push({ value: SurveyStates.Delivery, text: translate(`surveyStatus.${SurveyStates.Delivery}`) });
            options.push({ value: SurveyStates.Completed, text: translate(`surveyStatus.${SurveyStates.Completed}`) });
        }
        if (userRole === Roles.Administrator && id) {
            options.push({ value: SurveyStates.Deleted, text: translate(`surveyStatus.${SurveyStates.Deleted}`) });
        }
        return options;
    }

    function saveClicked(e: React.FormEvent | React.MouseEvent, shouldGoToEdit?: boolean) {
        e.preventDefault();
        captureMessage('Survey save clicked', { extra: { objectId: formState.objectId, batchId: formState.batchId } });

        validate(async (validationError: unknown) => {
            if (validationError) {
                return;
            }
            let url = '/api/surveys';
            if (id) {
                url += `/${id}`;
            }
            const data: Record<string, unknown> = {
                surveryedOn: formState.surveryedOn,
                status: formState.status,
                inspectionStandardType: formState.inspectionStandardType,
                description: formState.description,
                objectId: formState.objectId,
                operatorCompanyId: formState.operatorCompanyId,
                surveyorCompanyId: formState.surveyorCompanyId,
                isFmecaAvailable: formState.inspectionStandardType === InspectionStandardTypes.Fmeca ? formState.isFmecaAvailable : false,
                isCraAvailable: formState.inspectionStandardType === InspectionStandardTypes.Fmeca ? formState.isCraAvailable : false,
                batchId: formState.batchId,
                referenceNumber: formState.referenceNumber,
                labels: formState.labels,
                reportDeadlineOnDate: formState.reportDeadlineOnDate,
            };

            captureMessage('Post new survey', { extra: { objectId: formState.objectId, batchId: formState.batchId } });
            const result = await fetchPost(url, {}, data);
            const savedJson = result.json as { id?: string } | undefined;
            const savedId = savedJson?.id;

            const newSurveyId: string | undefined = id ?? savedId;
            if (newSurveyId) {
                const scopeOk = await saveScope(newSurveyId, definedScope, exclusions);
                if (!scopeOk) {
                    // The survey itself is saved, only the scope-write failed. Surface this
                    // to the user (rather than silently navigating away) so they know to
                    // retry via "Edit scope" later. We don't roll the survey back —
                    // it's valid data; missing scope = full scope by default.
                    captureMessage('Survey saved but scope write failed', { extra: { surveyId: newSurveyId } });
                    window.alert(translate('survey.scopeSaveFailed'));
                }
            }

            if (shouldGoToEdit && savedId) {
                if (formState.inspectionStandardType === InspectionStandardTypes.Fmeca) {
                    navigate(`/surveys/${savedId}/analysis`);
                } else if (formState.inspectionStandardType === InspectionStandardTypes.Memo) {
                    navigate(`/surveys/${savedId}/summaryAndAdvice`);
                } else if (formState.inspectionStandardType === InspectionStandardTypes.Model3d) {
                    navigate(`/surveys/${savedId}/pointcloud`);
                } else {
                    navigate(`/surveys/${savedId}/editReport`);
                }
            } else {
                navigate(-1);
            }
        });
    }

    const isEditMode = Boolean(id);
    // Scope + explanation tabs are also useful while creating a new survey,
    // as long as the user has already picked an object. Without this the
    // user would have to save once before being allowed to scope.
    const showTabs = isEditMode || Boolean(formState.objectId);
    const renderTabHeader = () => (
        <ul className="nav nav-list nav-list-topbar" style={{ marginBottom: 10, paddingLeft: 0, listStyle: 'none', display: 'flex' }}>
            <li className={classNames('pr20', { active: activeTab === 'details' })}>
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        setActiveTab('details');
                    }}
                >
                    {translate('common.description')}
                </a>
            </li>
            <li className={classNames('pr20', { active: activeTab === 'scope' })}>
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        setActiveTab('scope');
                    }}
                >
                    {translate('survey.scopeTab')}
                </a>
            </li>
            <li className={classNames('pr20', { active: activeTab === 'explanation' })}>
                <a
                    href="#"
                    onClick={(e) => {
                        e.preventDefault();
                        setActiveTab('explanation');
                    }}
                >
                    {translate('survey.explanationTab')}
                </a>
            </li>
        </ul>
    );

    return (
        <div>
            <TopbarLeftOnly>
                <TopbarCrumbActive icon="fa-camera" title={formState.objectName} />
                <TopbarCrumb>
                    <Link to={`/surveys/${id}`}>{translate('common.surveyDetails')}</Link>
                </TopbarCrumb>
                <TopbarCrumb>
                    <Link to={`/objects/${formState.objectId}`}>{translate('common.objectDetails')}</Link>
                </TopbarCrumb>
                <TopbarCrumb>
                    <Link to="/objects">{translate('common.objects')}</Link>
                </TopbarCrumb>
            </TopbarLeftOnly>

            <Content>
                <Tray>
                    <div className="admin-form theme-primary mw800 center-block">
                        {!formState.areThereActiveBatches && useBatches && (
                            <Alert severity="info">{translate('batch.noActiveBatchesForObject')}</Alert>
                        )}
                        <div className="panel">
                            <form method="post" action="/" id="admin-form" onSubmit={(e) => saveClicked(e)}>
                                {showTabs && renderTabHeader()}
                                <PanelBody>
                                    {activeTab === 'details' && (
                                        <>
                                            <EditPanelSection>
                                                <EditPanelInput
                                                    name="description"
                                                    placeholder={useProductTitle ? translate('product.description') : translate('survey.description')}
                                                    value={formState.description}
                                                    icon="fa fa-tag"
                                                    handleChange={(value) => void handleChangeWithSideEffects(value, 'description')}
                                                />
                                            </EditPanelSection>
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={6}>
                                                    <EditPanelSelect
                                                        name="status"
                                                        value={formState.status}
                                                        topLabel={translate('common.status')}
                                                        handleChange={(value) => void handleChangeWithSideEffects(value, 'status')}
                                                        options={surveyStates()}
                                                    />
                                                </EditPanelCell>
                                                <EditPanelCell rowCount={6}>
                                                    <EditPanelInput
                                                        name="referenceNumber"
                                                        value={formState.referenceNumber}
                                                        placeholder={translate('survey.referenceNumber')}
                                                        topLabel={translate('survey.referenceNumber')}
                                                        handleChange={(value) => void handleChangeWithSideEffects(value, 'referenceNumber')}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={3}>
                                                    <EditPanelDatePicker
                                                        name="surveryedOn"
                                                        value={formState.surveryedOn ?? null}
                                                        handleChange={(value) => void handleChangeWithSideEffects(value, 'surveryedOn')}
                                                        topLabel={translate('survey.surveyedOn')}
                                                        placeholder={translate('common.date')}
                                                    />
                                                    <ValidationHelpText message={getValidationMessages('surveryedOn')} />
                                                </EditPanelCell>

                                                <EditPanelCell rowCount={3}></EditPanelCell>

                                                <EditPanelCell rowCount={3}>
                                                    <EditPanelDatePicker
                                                        name="reportDeadlineOnDate"
                                                        value={formState.reportDeadlineOnDate}
                                                        handleChange={(value, name) => void handleChangeWithSideEffects(value, name)}
                                                        topLabel={translate('survey.reportDeadlineOnDate')}
                                                        placeholder={translate('common.date')}
                                                    />
                                                </EditPanelCell>
                                            </EditPanelRow>
                                            {useNen2767Advanced && (
                                                <>
                                                    {useBatches && (
                                                        <>
                                                            {((userRole === Roles.Administrator || isClient) && (
                                                                <>
                                                                    {formState.batches.length !== 0 && (
                                                                        <>
                                                                            <PanelHeader text={translate('batch.batch')} />
                                                                            <EditPanelRow>
                                                                                <EditPanelCell rowCount={12}>
                                                                                    <EditPanelSelect
                                                                                        name="batchId"
                                                                                        value={formState.batchId}
                                                                                        handleChange={(value) =>
                                                                                            void handleChangeWithSideEffects(value, 'batchId')
                                                                                        }
                                                                                        options={[
                                                                                            { value: '', text: translate('batch.notSelected') },
                                                                                            ...Array.from(
                                                                                                new Set(
                                                                                                    formState.batches.map((i) => ({
                                                                                                        value: i.id,
                                                                                                        text: `${i.name} (${translate(
                                                                                                            `inspectionStandard.${i.inspectionStandardType}`,
                                                                                                        )})`,
                                                                                                    })),
                                                                                                ),
                                                                                            ),
                                                                                        ]}
                                                                                    />
                                                                                </EditPanelCell>
                                                                            </EditPanelRow>
                                                                        </>
                                                                    )}
                                                                    <PanelHeader text={translate('common.inspectionStandard')} />
                                                                    <EditPanelRow>
                                                                        <EditPanelCell rowCount={6}>
                                                                            <EditPanelSelect
                                                                                name="inspectionStandardType"
                                                                                value={formState.inspectionStandardType}
                                                                                handleChange={(value) =>
                                                                                    void handleChangeWithSideEffects(value, 'inspectionStandardType')
                                                                                }
                                                                                placeholder={translate(
                                                                                    'common.selectItem',
                                                                                    translate('common.inspectionStandard'),
                                                                                )}
                                                                                notValid={hasValidationErrors(
                                                                                    getValidationMessages('inspectionStandardType'),
                                                                                )}
                                                                                options={getInspectionStandardTypeOptions(translate)}
                                                                            />
                                                                        </EditPanelCell>
                                                                    </EditPanelRow>
                                                                </>
                                                            )) || (
                                                                <>
                                                                    {formState.batches.length !== 0 && (
                                                                        <>
                                                                            <PanelHeader text={translate('batch.batch')} />
                                                                            <EditPanelRow>
                                                                                <EditPanelCell rowCount={12}>
                                                                                    {(formState.batches.length === 1 && (
                                                                                        <>
                                                                                            {formState.batches[0]!.name} (
                                                                                            {translate(
                                                                                                `inspectionStandard.${
                                                                                                    formState.batches[0]!.inspectionStandardType
                                                                                                }`,
                                                                                            )}
                                                                                            )
                                                                                        </>
                                                                                    )) || (
                                                                                        <EditPanelSelect
                                                                                            name="batchId"
                                                                                            value={formState.batchId}
                                                                                            handleChange={(value) =>
                                                                                                void handleChangeWithSideEffects(value, 'batchId')
                                                                                            }
                                                                                            options={formState.batches.map((i) => ({
                                                                                                value: i.id,
                                                                                                text: `${i.name} (${translate(
                                                                                                    `inspectionStandard.${i.inspectionStandardType}`,
                                                                                                )})`,
                                                                                            }))}
                                                                                        />
                                                                                    )}
                                                                                </EditPanelCell>
                                                                            </EditPanelRow>
                                                                        </>
                                                                    )}
                                                                </>
                                                            )}
                                                        </>
                                                    )}
                                                    {!useBatches && (
                                                        <>
                                                            <PanelHeader text={translate('common.inspectionStandard')} />
                                                            <EditPanelRow>
                                                                <EditPanelCell rowCount={6}>
                                                                    <EditPanelSelect
                                                                        name="inspectionStandardType"
                                                                        value={formState.inspectionStandardType}
                                                                        handleChange={(value) =>
                                                                            void handleChangeWithSideEffects(value, 'inspectionStandardType')
                                                                        }
                                                                        placeholder={translate(
                                                                            'common.selectItem',
                                                                            translate('common.inspectionStandard'),
                                                                        )}
                                                                        notValid={hasValidationErrors(
                                                                            getValidationMessages('inspectionStandardType'),
                                                                        )}
                                                                        options={getInspectionStandardTypeOptions(translate)}
                                                                    />
                                                                    <ValidationHelpText message={getValidationMessages('inspectionStandardType')} />
                                                                </EditPanelCell>
                                                            </EditPanelRow>
                                                        </>
                                                    )}
                                                    {formState.inspectionStandardType === InspectionStandardTypes.Fmeca && (
                                                        <>
                                                            <PanelHeader text={translate('common.settings')} />
                                                            <EditPanelSection>
                                                                <EditPanelSwitch
                                                                    name="isFmecaAvailable"
                                                                    placeholder={translate('survey.isFmecaAvailable')}
                                                                    value={formState.isFmecaAvailable}
                                                                    handleChange={(value) =>
                                                                        void handleChangeWithSideEffects(value, 'isFmecaAvailable')
                                                                    }
                                                                />
                                                            </EditPanelSection>
                                                            <EditPanelSection>
                                                                <EditPanelSwitch
                                                                    name="isCraAvailable"
                                                                    placeholder={translate('survey.isCraAvailable')}
                                                                    value={formState.isCraAvailable}
                                                                    handleChange={(value) =>
                                                                        void handleChangeWithSideEffects(value, 'isCraAvailable')
                                                                    }
                                                                />
                                                            </EditPanelSection>
                                                        </>
                                                    )}
                                                </>
                                            )}

                                            <PanelHeader text={translate('common.labels')} />
                                            <EditPanelRow>
                                                <EditPanelCell rowCount={12}>
                                                    <EditPanelFilterCreateSelect
                                                        name="labels"
                                                        placeholder={translate('common.selectItem', translate('common.labels'))}
                                                        value={formState.labels}
                                                        options={formState.knownLabels}
                                                        handleChange={(value, name) => void handleChangeWithSideEffects(value, name)}
                                                    />
                                                    <div className="mt15">
                                                        <EditPanelExplanation text={translate('survey.labelsExpl')} />
                                                    </div>
                                                </EditPanelCell>
                                            </EditPanelRow>
                                        </>
                                    )}

                                    {activeTab === 'scope' && (
                                        <div className="p20">
                                            {formState.objectId ? (
                                                <ScopePicker
                                                    surveyId={id}
                                                    objectId={formState.objectId}
                                                    exclusions={exclusions}
                                                    onChange={(next) => setExclusions(next)}
                                                />
                                            ) : (
                                                <div className="text-muted">{translate('survey.scopePlaceholder')}</div>
                                            )}
                                        </div>
                                    )}

                                    {activeTab === 'explanation' && (
                                        <div className="p20">
                                            <MarkdownEditor
                                                value={definedScope ?? ''}
                                                placeholder={translate('survey.explanationPlaceholder')}
                                                onChange={(value) => setDefinedScope(value === '' ? null : value)}
                                            />
                                        </div>
                                    )}
                                </PanelBody>

                                <EditPanelButtons footer>
                                    <button type="button" disabled={formState.submitting} className="button mr10" onClick={() => navigate(-1)}>
                                        {' '}
                                        {translate('common.cancel')}{' '}
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={
                                            formState.submitting ||
                                            (!formState.areThereActiveBatches &&
                                                !isClient &&
                                                (userRole === Roles.Surveyor || userRole === Roles.Operator))
                                        }
                                        className={id ? 'button btn-primary' : 'button mr10'}
                                    >
                                        {' '}
                                        {translate('common.save')}
                                    </button>
                                    {!id && (
                                        <button
                                            type="button"
                                            disabled={
                                                formState.submitting ||
                                                (!formState.areThereActiveBatches &&
                                                    !isClient &&
                                                    (userRole === Roles.Surveyor || userRole === Roles.Operator))
                                            }
                                            onClick={(e) => saveClicked(e, true)}
                                            className="button btn-primary"
                                        >
                                            {' '}
                                            {translate('survey.saveSurveyAndAddVisuals')} <span className="fa fa-angle-double-right pl5"></span>
                                        </button>
                                    )}
                                </EditPanelButtons>
                            </form>
                        </div>
                    </div>
                </Tray>
            </Content>
        </div>
    );
}

// eslint-disable-next-line import/no-default-export
export default SurveyAddEditComponent;
